import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { storeApi } from '../services/api';
import { Input, Label, Button } from '@redpanda/ui';
import toast from 'react-hot-toast';

interface DayHours {
  open: string;
  close: string;
  closed?: boolean;
}

interface OperatingHours {
  monday?: DayHours;
  tuesday?: DayHours;
  wednesday?: DayHours;
  thursday?: DayHours;
  friday?: DayHours;
  saturday?: DayHours;
  sunday?: DayHours;
  holidays?: string[];
}

interface Props {
  storeId: string;
}

export interface OperatingHoursEditorRef {
  save: () => Promise<void>;
}

const defaultDayHours: DayHours = { open: '09:00', close: '21:00', closed: false };

const OperatingHoursEditor = forwardRef<OperatingHoursEditorRef, Props>(({ storeId }, ref) => {
  const { t } = useTranslation(['components', 'translation']);

  const dayLabels: { [key: string]: string } = {
    monday: t('components:operatingHours.days.monday'),
    tuesday: t('components:operatingHours.days.tuesday'),
    wednesday: t('components:operatingHours.days.wednesday'),
    thursday: t('components:operatingHours.days.thursday'),
    friday: t('components:operatingHours.days.friday'),
    saturday: t('components:operatingHours.days.saturday'),
    sunday: t('components:operatingHours.days.sunday'),
  };

  const [hours, setHours] = useState<OperatingHours>({
    monday: defaultDayHours,
    tuesday: defaultDayHours,
    wednesday: defaultDayHours,
    thursday: defaultDayHours,
    friday: defaultDayHours,
    saturday: defaultDayHours,
    sunday: defaultDayHours,
    holidays: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [newHoliday, setNewHoliday] = useState('');

  useImperativeHandle(ref, () => ({
    save: async () => {
      await storeApi.updateOperatingHours(storeId, hours);
    },
  }));

  useEffect(() => {
    loadOperatingHours();
  }, [storeId]);

  const loadOperatingHours = async () => {
    try {
      setIsLoading(true);
      const response = await storeApi.getOperatingHours(storeId);
      setHours(response.data);
    } catch (error: any) {
      console.error('Failed to load operating hours:', error);
      toast.error(t('components:operatingHours.loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDayChange = (day: keyof OperatingHours, field: 'open' | 'close' | 'closed', value: string | boolean) => {
    setHours((prev) => ({
      ...prev,
      [day]: {
        ...(prev[day] as DayHours),
        [field]: value,
      },
    }));
  };

  const handleAddHoliday = () => {
    if (!newHoliday) return;

    // 驗證日期格式 YYYY-MM-DD
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(newHoliday)) {
      toast.error(t('components:operatingHours.invalidDateFormat'));
      return;
    }

    setHours((prev) => ({
      ...prev,
      holidays: [...(prev.holidays || []), newHoliday],
    }));
    setNewHoliday('');
  };

  const handleRemoveHoliday = (holiday: string) => {
    setHours((prev) => ({
      ...prev,
      holidays: (prev.holidays || []).filter((h) => h !== holiday),
    }));
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-slate-800 shadow rounded-lg p-4 md:p-6">
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">{t('translation:common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800 shadow rounded-lg p-4 md:p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">{t('components:operatingHours.title')}</h2>

      {/* 每日營業時間 */}
      <div className="space-y-4 mb-8">
        {Object.keys(dayLabels).map((day) => {
          const dayKey = day as keyof OperatingHours;
          const dayData = hours[dayKey] as DayHours | undefined;

          return (
            <div key={day} className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
              {/* 星期標籤 */}
              <div className="md:col-span-2">
                <Label className="font-medium text-gray-900 dark:text-white">
                  {dayLabels[day]}
                </Label>
              </div>

              {/* 公休選項 */}
              <div className="md:col-span-2">
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={dayData?.closed || false}
                    onChange={(e) => handleDayChange(dayKey, 'closed', e.target.checked)}
                    className="rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('components:operatingHours.closed')}</span>
                </label>
              </div>

              {/* 開始時間 */}
              <div className="md:col-span-3">
                <div className="flex items-center space-x-2">
                  <Label htmlFor={`${day}-open`} className="text-sm">{t('components:operatingHours.openTime')}</Label>
                  <Input
                    id={`${day}-open`}
                    type="time"
                    value={dayData?.open || '09:00'}
                    onChange={(e) => handleDayChange(dayKey, 'open', e.target.value)}
                    disabled={dayData?.closed}
                    className="flex-1 dark:[color-scheme:dark]"
                  />
                </div>
              </div>

              {/* 結束時間 */}
              <div className="md:col-span-3">
                <div className="flex items-center space-x-2">
                  <Label htmlFor={`${day}-close`} className="text-sm">{t('components:operatingHours.closeTime')}</Label>
                  <Input
                    id={`${day}-close`}
                    type="time"
                    value={dayData?.close || '21:00'}
                    onChange={(e) => handleDayChange(dayKey, 'close', e.target.value)}
                    disabled={dayData?.closed}
                    className="flex-1 dark:[color-scheme:dark]"
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 公休日設定 */}
      <div className="border-t border-gray-200 dark:border-slate-700 pt-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">{t('components:operatingHours.holidays.title')}</h3>

        {/* 新增公休日 */}
        <div className="flex items-center space-x-2 mb-4">
          <div className="flex-1 max-w-xs">
            <Label htmlFor="new-holiday">{t('components:operatingHours.holidays.addLabel')}</Label>
            <Input
              id="new-holiday"
              type="date"
              value={newHoliday}
              onChange={(e) => setNewHoliday(e.target.value)}
              placeholder="YYYY-MM-DD"
              className="dark:[color-scheme:dark]"
            />
          </div>
          <Button
            type="button"
            onClick={handleAddHoliday}
            variant="secondary"
            className="mt-6"
          >
            {t('translation:common.add')}
          </Button>
        </div>

        {/* 公休日列表 */}
        {hours.holidays && hours.holidays.length > 0 ? (
          <div className="space-y-2">
            {hours.holidays.map((holiday) => (
              <div
                key={holiday}
                className="flex items-center justify-between bg-gray-50 dark:bg-slate-900 rounded px-4 py-2"
              >
                <span className="text-gray-900 dark:text-white font-mono">{holiday}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveHoliday(holiday)}
                  className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm"
                >
                  {t('components:operatingHours.holidays.remove')}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-400 text-sm">{t('components:operatingHours.holidays.noHolidays')}</p>
        )}
      </div>
    </div>
  );
});

OperatingHoursEditor.displayName = 'OperatingHoursEditor';

export default OperatingHoursEditor;
